import React from 'react';
import SEO from '../components/SEO';
import './Contact.css';

/**
 * Contact Page Component
 * Provides contact information and communication channels
 * Shows email, website, and response time expectations
 * Includes SEO meta tags for optimal search engine visibility
 */
function Contact() {
  return (
    <div className="page-container contact-page">
      {/* SEO Meta Tags */}
      <SEO 
        title="Contact lockitup – Support & Feedback"
        description="Contact lockitup for support, suggestions, or feedback. We reply within 24–48 hours."
        keywords="contact lockitup, support, feedback, help, customer service"
        ogTitle="Contact lockitup – Support & Feedback"
        ogDescription="Contact lockitup for support, suggestions, or feedback. We reply within 24–48 hours."
        ogUrl="https://www.lockitup.tech/contact"
      />
      {/* Page Header */}
      <section className="page-hero">
        <div className="container">
          <div className="page-hero-content">
            {/* Mail Icon */}
            <div className="page-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="20" width="60" height="40" rx="4" stroke="#27AE60" strokeWidth="3" fill="rgba(39, 174, 96, 0.1)" />
                <path d="M10 25L40 45L70 25" stroke="#F1C40F" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 55L28 40M70 55L52 40" stroke="#F1C40F" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            
            {/* Page Title */}
            <h1 className="page-title">Contact Us</h1>
            <p className="page-subtitle">We'd love to hear from you</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="page-content">
        <div className="container">
          <div className="content-wrapper">
            
            {/* Introduction */}
            <div className="intro-section">
              <p className="intro-text">
                Have questions, suggestions, or feedback?<br />
                We'd love to hear from you.
              </p>
            </div>

            {/* Contact Methods */}
            <div className="contact-cards">
              
              {/* Website Card */}
              <div className="contact-card">
                <div className="contact-icon">
                  <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="25" cy="25" r="18" stroke="#F1C40F" strokeWidth="2.5" fill="rgba(241, 196, 15, 0.1)" />
                    <path d="M7 25H43M25 7C25 7 18 14 18 25C18 36 25 43 25 43M25 7C25 7 32 14 32 25C32 36 25 43 25 43" stroke="#F1C40F" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="contact-label">Website</h3>
                <a href="https://www.lockitup.tech" className="contact-value website-link" target="_blank" rel="noopener noreferrer">
                  www.lockitup.tech
                </a>
              </div>

            </div>

            {/* Response Time Info */}
            <div className="info-banner">
              <div className="info-banner-icon">
                <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="17.5" cy="17.5" r="15.5" stroke="#27AE60" strokeWidth="2.5" fill="rgba(39, 174, 96, 0.1)" />
                  <path d="M17.5 10V17.5L23 20.5" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="info-banner-content">
                <h3 className="info-banner-title">Response Time</h3>
                <p className="info-banner-text">We usually reply within 24–48 hours.</p>
              </div>
            </div>

            {/* Feedback Section */}
            <div className="feedback-section">
              <h2 className="feedback-title">What You Can Contact Us About:</h2>
              <div className="feedback-grid">
                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="15" cy="15" r="12" stroke="#27AE60" strokeWidth="2" />
                      <path d="M15 10V16M15 20H15.01" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span>Technical support</span>
                </div>

                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 5L18 12L25 13L20 18L21 25L15 21L9 25L10 18L5 13L12 12L15 5Z" stroke="#27AE60" strokeWidth="2" strokeLinejoin="round" fill="rgba(39, 174, 96, 0.1)" />
                    </svg>
                  </div>
                  <span>Feature suggestions</span>
                </div>

                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 15L12 22L27 7" stroke="#27AE60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span>Bug reports</span>
                </div>

                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="6" stroke="#27AE60" strokeWidth="2" fill="rgba(39, 174, 96, 0.1)" />
                      <circle cx="22" cy="22" r="6" stroke="#27AE60" strokeWidth="2" fill="rgba(39, 174, 96, 0.1)" />
                      <path d="M15 10H22M10 15V22" stroke="#27AE60" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span>Partnership inquiries</span>
                </div>

                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 3C15 3 7 7 7 12C7 15.314 9.686 18 13 18C13 18 13 22 18 25C18 25 25 22 25 15C25 10 18 3 18 3C18 3 16 6 15 8C14 6 15 3 15 3Z" stroke="#27AE60" strokeWidth="2" strokeLinejoin="round" fill="rgba(39, 174, 96, 0.1)" />
                    </svg>
                  </div>
                  <span>General feedback</span>
                </div>

                <div className="feedback-item">
                  <div className="feedback-icon">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="10" width="18" height="14" rx="2" stroke="#27AE60" strokeWidth="2" fill="rgba(39, 174, 96, 0.1)" />
                      <path d="M10 10V8C10 6.34315 11.3431 5 13 5H17C18.6569 5 20 6.34315 20 8V10" stroke="#27AE60" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="15" cy="17" r="1.5" fill="#27AE60" />
                    </svg>
                  </div>
                  <span>Security concerns</span>
                </div>
              </div>
            </div>

            {/* Thank You Message */}
            <div className="thank-you-box">
              <h2 className="thank-you-title">Thank you for using lockitup</h2>
              <p className="thank-you-text">Protecting your privacy is our goal.</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
